/* eslint-disable */
import { Dispatch, FormEvent, SetStateAction, useEffect, useRef, useState } from 'react';
import './ZiChart.scss';
import {
	Button,
	Checkbox,
	FormGroup,
	NumberInput,
	Popover,
	Tooltip,
	Text,
} from '@patternfly/react-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	fa1,
	fa2,
	faChartArea,
	faChartBar,
	faChartLineUp,
	faChartPyramid,
	faChartScatter,
	faChartScatterBubble,
	faChartSimple,
	faChartSimpleHorizontal,
	faChevronCircleUp,
	faChevronsDown,
	faCloudWord,
	faCube,
	faDiagramNested,
	faDiagramSankey,
	faFilter,
	faGauge,
	faGear,
	faHeat,
	faInfoCircle,
	faSparkles,
	faLollipop,
	faPalette,
	faPieChart,
	faCircleDot,
	faWarning,
	faSlider,
	faChartTreeMap,
} from '@fortawesome/pro-regular-svg-icons';
import { ChartBuilder, returnHighChartsChartType } from '../../builders/chart-builder';
import Loader from '../util/Loader';
import { useHighChart } from '../../hooks/HighChartHooks';
import { ChartSeries, ChartSeriesChartTypes } from '../../types/charts/chart-options';
import { ColorResult, TwitterPicker } from 'react-color';
import tinycolor from 'tinycolor2';
import EmptyResults from '../EmptyResults';
import { TUnitType, UnitType } from '../../api/analytics/UnitType';
import { useParams } from 'react-router-dom';
import { DraggableMenuItemData, Size } from '../../types/databuilder/databuilder';
import { adjustMonthsInRange, getChartSeriesData } from '../../helpers/chart.helper';
import { ZiChartSettings } from '../../types/charts/chart-settings';
import { TKeyMeasureFact } from '../../api/analytics/KeyMeasureFact';
import { TChart } from '../../api/charts/Chart';
import { useMount } from 'react-use';
import { ChartTypesEnum } from '../../enums/chart-types.enum';
import { ChartWithCategories } from '../../api/types/chart-with-categories';
import { DimSettings, TDataframeFilter, TDateRange } from '../../api/types';
import { TZoneSetting, ZoneSetting, ZoneSettingEnum } from '../../api/zone/ZoneSetting';
import CustomColorCarousel from '../color/CustomColorCarousel';
import { useApplication } from '../user/ApplicationProvider';
import { DimensionAttribute } from '../../api/analytics/DimensionAttribute';
import { EntityTypesEnum } from '../../enums/entity-types.enum';
import { DataImportEntityTypeEnum } from '../../api/admin/AdminActions';
import { Dimension, DimensionTypeEnum, TDimension } from '../../api/analytics/Dimension';
import { DimensionOptions } from '../../types/dataframes/dataframe-dimension-options';
import { OptionsBuilderItemTypes } from '../../types/dataframes/options-builder-item-types';
import { useCommonStoreContext } from '../common-store/CommonStoreProvider';
import { DatePeriodFormatter } from '../date-period-selector/DatePeriodFormatter';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import Highcharts from 'highcharts';

export type ZiChartProps = {
	isConfigMode: boolean;
	displayChart?: boolean;
	title?: string;
	valueTitle?: string;
	categoryTitle: string;
	chartRetrevialData: any[];
	dimConfigValues: DimSettings[];
	chartData?: TChart;
	chartType?: ChartSeriesChartTypes;
	setChartType?: Dispatch<SetStateAction<ChartSeriesChartTypes>>;
	secondChartType?: ChartSeriesChartTypes;
	setSecondChartType?: Dispatch<SetStateAction<ChartSeriesChartTypes>>;
	setChartSettings: Dispatch<SetStateAction<ZiChartSettings>>;
	chartSettings: ZiChartSettings;
	isLoading?: boolean;
	transparentBackground?: boolean;
	size?: Size | undefined;
	setChart?: Dispatch<SetStateAction<TChart | null>>;
	setChartRetrevialData?: Dispatch<SetStateAction<any[] | undefined>>;
	unitType?: TUnitType;
	setSelectedUnitType?: Dispatch<SetStateAction<TUnitType | undefined>>;
	selectedUnitType?: TUnitType;
	selected2ndUnitType?: TUnitType;
	selectedNumDecimals?: number;
	selected2ndNumDecimals?: number;
	setHasLoadedSideBar?: Dispatch<SetStateAction<boolean>>;
	isSingleSeries?: boolean;
	containerProps?: any;
	setDrillSequenceOpen?: Dispatch<SetStateAction<boolean>>;
	setInsightsOpen?: Dispatch<SetStateAction<boolean>>;
	setMustResetSelectedChart?: Dispatch<SetStateAction<boolean>>;
	mustResetSelectedChart?: boolean;
	droppedFieldsCount?: number;
	droppedFactsCount?: number;
	isExistingChart?: boolean;
	isDataRetrievalLoading?: boolean;
	ApplyDrilldownDrillIn?: (name: string, isKMF: boolean) => void;
	drillLevel?: number;
	resetDrillSequence?: () => void;
	keyMeasureFact?: TKeyMeasureFact | null | undefined;
	sethasMultipleRecords?: Dispatch<SetStateAction<boolean>>;
	setIsOdometer?: Dispatch<SetStateAction<boolean>>;
	showLimitWarning?: boolean;
	setShowLimitWarning?: Dispatch<SetStateAction<boolean>>;
	grandTotal?: [{ [key: string]: number }];
	droppedFacts?: DraggableMenuItemData[];
	droppedFields?: DraggableMenuItemData[];
	unitTypes?: TUnitType[];
	chartRenderedCallback: () => void;
	isChartOnly?: boolean;
	noAnimation?: boolean;
	filters?: DraggableMenuItemData[] | TDataframeFilter[];
	chartSeriesHasChanged?: boolean;
};

const ZiChart = (props: ZiChartProps) => {
	// hooks
	const { options, setOptions, chartRender, getChart } = useHighChart(
		props.chartRenderedCallback
	);

	// region state
	const [chartTypeOpen, setChartTypeOpen] = useState(false);
	const [, setDrillSequenceOpen] = useState(false);
	const [, setColorsOpen] = useState(false);
	const [, setChartSettingsOpen] = useState(false);
	const [, setInsightsPinned] = useState(false);

	// chart settings
	const [backgroundColor, setBackgroundColor] = useState('#fff');
	const [chartSeriesColor1, setChartSeriesColor1] = useState('#0693E3');
	const [chartSeriesColor2, setChartSeriesColor2] = useState('#bcbcbe');
	const [hasLoadedChartOptions, setHasLoadedChartOptions] = useState<boolean>(false);
	const [overrideDefinedColors, setOverrideDefinedColors] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(false);

	// chart state
	const [title, setTitle] = useState<string>(props.title ?? '');
	const [categoryTitle, setCategoryTitle] = useState<string>(props.categoryTitle ?? '');
	const [categories] = useState<string[]>([]);
	const [seriesData, setSeriesData] = useState<ChartSeries[]>([]);
	const [_dynamicChartSeries, _setDynamicChartSeries] =
		useState<[number[], number[], number[]]>();
	const [xAxisArray, setXaxisArray] = useState<string[]>([]);
	const [yAxisArray, setYaxisArray] = useState<string[]>([]);
	const [lookupArray, setLookupArray] = useState<(string | number)[][]>([]);
	const [selectedChartTypeButton, setSelectedChartTypeButton] = useState<string>();
	const [executionCount, setExecutionCount] = useState(0);
	const { chartId, dashboardId } = useParams();
	const [currentChartSeriesNumber, setCurrentChartSeriesNumber] = useState(1);
	const isMultiSeries = props.droppedFields && props.droppedFields.length > 1;
	const [previousSeriesState, setPreviousSeriesState] = useState(
		isMultiSeries ? 'multi' : 'single'
	);
	const [displayStateNoResults, setDisplayStateNoResults] = useState<string>('none');
	const [colorPalette, setColorPalette] = useState<string[]>([]);
	const [enableHighchartsAccessibility, setenableHighchartsAccessibility] =
		useState<boolean>(true);
	const [hexArrayValue1, sethexArrayValue1] = useState<string>();
	const { zoneSettings } = useApplication();

	const previousSelectedStartPeriod = localStorage.getItem('currentSelectedStartPeriod');
	const previousSelectedEndPeriod = localStorage.getItem('currentSelectedEndPeriod');
	const hasPreviouslySelectedPeriod =
		previousSelectedStartPeriod != null || previousSelectedEndPeriod != null;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const previouslySelectedStartDate: TDateRange | null =
		hasPreviouslySelectedPeriod && previousSelectedStartPeriod
			? JSON.parse(previousSelectedStartPeriod)
			: null;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const previouslySelectedEndDate: TDateRange | null =
		hasPreviouslySelectedPeriod && previousSelectedEndPeriod
			? JSON.parse(previousSelectedEndPeriod)
			: null;
	const { currentDatePeriods } = useApplication();
	const [selectedStartDate, setSelectedStartDate] = useState<TDateRange | undefined>(
		hasPreviouslySelectedPeriod && previouslySelectedStartDate
			? previouslySelectedStartDate
			: currentDatePeriods.find((dp) => dp.period == 3)
	);
	const [selectedEndDate, setSelectedEndDate] = useState<TDateRange | undefined>(
		hasPreviouslySelectedPeriod && previouslySelectedEndDate
			? previouslySelectedEndDate
			: currentDatePeriods.find((dp) => dp.period == 3)
	);
	const humanifyChartNumbers = localStorage.getItem('humanifyChartNumbers') == 'True';
	const [actualHumanifyChartNumbers, setActualHumanifyChartNumbers] =
		useState<boolean>(humanifyChartNumbers);
	const [loadingChartOverride, setLoadingChartOverride] = useState<boolean>(false);
	const [isPageLoadChartView, setIsPageLoadChartView] = useState<boolean>(false);

	useMount(() => {
		initChartColors();
		localStorage.removeItem('currentSeriesState');
	});

	useEffect(() => {
		//highcharts loading overlay for dashboard chart widgets
		//primarily heatmap, odometer, wordcloud & sankey are considered exotic charts and take longer to load in poorer network
		if (dashboardId && !chartId) {
			const chart = getChart();
			if (!chart) return;

			chart.container.style.backgroundColor = '#fff';
			chart.container.style.opacity = '1';

			chart.showLoading(`
			<div class="loader" style="display:flex; justify-content:center; align-items:center; height:100%; position: relative; bottom:50%">
			<span class="pf-c-spinner pf-m-xl" role="status" aria-label="Loading">
				<span class="pf-c-spinner__clipper"></span>
				<span class="pf-c-spinner__lead-ball"></span>
				<span class="pf-c-spinner__tail-ball"></span>
			</span>
			</div>
		`);

			let hidden = false;

			const hideLoadingOnce = () => {
				if (!hidden) {
					// wait a tick to ensure Highcharts internal rendering is done
					setTimeout(() => {
						chart.hideLoading();
						hidden = true;
					}, 50);
				}
			};

			Highcharts.addEvent(chart, 'load', hideLoadingOnce);
			Highcharts.addEvent(chart, 'render', hideLoadingOnce);

			// Failsafe: hide after 1s even if events never fire
			const failsafe = setTimeout(() => hideLoadingOnce(), 1000);

			return () => {
				Highcharts.removeEvent(chart, 'load', hideLoadingOnce);
				Highcharts.removeEvent(chart, 'render', hideLoadingOnce);
				clearTimeout(failsafe);
			};
		}
	}, [options.series]);

	useEffect(() => {
		if (chartId && !props.chartSettings.chartColor) {
			setChartSeriesColor1(hexArrayValue1 ?? '#0693E3');
		}
	}, [window.location.pathname, hexArrayValue1]);

	useEffect(() => {
		setIsLoading(true);

		const handler = setTimeout(() => {
			initChartColors();
			setOverrideDefinedColors(props.chartSettings.overrideValueDefinedColors);
			BuildDynamicChartSeries();
			setIsLoading(false);
		}, 500);

		return () => {
			clearTimeout(handler);
		};
	}, [
		props.setChart,
		props.setChartRetrevialData,
		props.chartRetrevialData,
		props.chartType,
		props.secondChartType,
		props.setSelectedUnitType,
		props.selectedUnitType,
		props.selectedNumDecimals,
		props.droppedFacts?.length,
		chartSeriesColor2,
		props.chartSettings?.showPercentages,
		overrideDefinedColors,
	]);

	const BuildDynamicChartSeries = () => {
		const chartSeriesData = getSeriesDataHelper();

		setXaxisArray(chartSeriesData.xAxisArray);
		if (chartSeriesData.yAxisArray && chartSeriesData.yAxisArray.length > 0) {
			setYaxisArray(chartSeriesData.yAxisArray);
		}

		props.setIsOdometer && props.setIsOdometer(props.chartType === 'odometer');

		if (chartSeriesData.seriesData[0]?.data?.length > 1) {
			props.sethasMultipleRecords && props.sethasMultipleRecords(true);
		} else {
			props.sethasMultipleRecords && props.sethasMultipleRecords(false);
		}

		setSeriesData(chartSeriesData.seriesData);
		setLookupArray(chartSeriesData.lookupData);
	};

	useEffect(() => {
		setTitle(props.title ?? '');
	}, [props.title, props.valueTitle, props.categoryTitle]);

	//workaround for heatmap not loading series data when chart changed to multi-series
	useEffect(() => {
		setChartTypeOpen(true);
		setTimeout(() => {
			setChartTypeOpen(false);
		}, 50);
	}, [props.droppedFieldsCount, props.droppedFactsCount]);

	const initChartColors = () => {
		let updatedChartColors =
			props.chartSettings.chartColor != ''
				? props.chartSettings.chartColor.trim().replaceAll(' ', '').split('|')
				: [];

		//first check to see if the color pallete is for chart with multiple facts dropped in (multi-fact chart)
		if (
			updatedChartColors.length > 0 &&
			updatedChartColors.length == 2 &&
			props.chartType !== 'clusteredbar' &&
			props.chartType !== 'clusteredcolumn'
		) {
			const series1Color = updatedChartColors[0];
			const series2Color = updatedChartColors[1];

			setChartSeriesColor1(series1Color);
			setChartSeriesColor2(series2Color);
		} else if (
			updatedChartColors.length > 0 &&
			props.chartType !== 'clusteredbar' &&
			props.chartType !== 'clusteredcolumn'
		) {
			const series1Color = updatedChartColors[0];
			setChartSeriesColor1(series1Color);
		}

		const highchartsAccessibleZoneSetting = zoneSettings.find(
			(x) => x.zone_setting_enum_id == ZoneSettingEnum.highchartsAccessibility
		);
		const highchartsColorPaletteZoneSetting = zoneSettings.find(
			(x) => x.zone_setting_enum_id == ZoneSettingEnum.defaultColorPalette
		);

		if (highchartsAccessibleZoneSetting && !highchartsAccessibleZoneSetting.enabled) {
			setenableHighchartsAccessibility(false);
		}

		if (highchartsColorPaletteZoneSetting && highchartsColorPaletteZoneSetting.value) {
			const colors = highchartsColorPaletteZoneSetting.value;

			const hexMap = JSON.parse(colors) as Record<string, string>;

			// Convert the object to an array of hex values ordered by key
			const hexArray = Object.keys(hexMap)
				.sort((a, b) => Number(a) - Number(b))
				.map((key) => hexMap[key]);

			setColorPalette(hexArray);
			sethexArrayValue1(hexArray[0]);

			if (
				((!chartId && !dashboardId && chartSeriesColor1 == '#0693E3') ||
					(dashboardId && chartSeriesColor1 == '#0693E3' && hexArray[0])) &&
				!props.chartSettings.chartColor
			) {
				setChartSeriesColor1(hexArray[0]);
			}
		}
	};

	const getSeriesDataHelper = () => {
		let dbChartType = (props.chartData?.chart_type ??
			props.chartType ??
			'column') as ChartSeriesChartTypes;

		if ((props.isSingleSeries && (props.chartData?.chart_type as string)) == 'solidgauge') {
			dbChartType = 'odometer';
		}
		if (props.isSingleSeries && props.chartData?.chart_type == 'treemap') {
			dbChartType = 'treemapsquarified';
		}
		if (!props.isSingleSeries && props.chartData?.chart_type == 'treemap') {
			dbChartType = 'treemapstripes';
		}

		const isSingleSeries =
			props.isSingleSeries ??
			[
				'bar',
				'column',
				'scatter',
				'wordcloud',
				'line',
				'packedbubble',
				'funnel',
				'area',
				'lollipop',
				'pyramid',
				'pie',
				'donut',
				'odometer',
				'columnrange',
				'treemapsquarified',
			].includes(dbChartType!!);

		const defaultUnitType = UnitType.Default();

		const yAxisFirstUnitType =
			props.droppedFacts && props.droppedFacts.length > 0
				? props.unitTypes?.find((x) =>
						props.droppedFacts ? x.id == props.droppedFacts[0].data?.unitType : 0
				  )
				: (defaultUnitType as TUnitType);

		const yAxisSecondUnitType =
			props.droppedFacts && props.droppedFacts.length > 1
				? props.unitTypes?.find((x) =>
						props.droppedFacts ? x.id == props.droppedFacts[1].data?.unitType : 0
				  )
				: (defaultUnitType as TUnitType);

		const enabledDimValueIds = props.droppedFields
			? props.droppedFields
					.filter((field) => field.data?.useSequence)
					.map((field) => {
						return field.data?.id ?? 0;
					})
			: [];

		const actualChartType = props.isChartOnly ? props.chartType : getChartType(dbChartType);

		const chartSeriesData = getChartSeriesData(
			props.chartRetrevialData,
			props.dimConfigValues,
			enabledDimValueIds,
			overrideDefinedColors,
			isSingleSeries,
			actualChartType,
			yAxisFirstUnitType,
			yAxisSecondUnitType,
			props.selectedNumDecimals,
			props.droppedFacts?.length,
			chartSeriesColor1,
			chartSeriesColor2,
			props.grandTotal,
			props.chartSettings.showPercentages,
			props.droppedFacts
		);

		if (props.droppedFacts && props.droppedFacts.length > 1) {
			const isBothYaxisColumn =
				props.secondChartType === 'column' && props.chartType == 'column';
			const isBothYaxisBar = props.secondChartType === 'bar' && props.chartType == 'bar';

			if (isBothYaxisColumn) {
				chartSeriesData.seriesData.map((data, idx) => {
					if (idx == 0) {
						data.type = 'bar';
						data.color = chartSeriesColor1;
					}
					if (idx == 1) {
						data.type = 'bar';
						data.color = chartSeriesColor2;
						data.dataLabels = { ...data.dataLabels, color: 'black' };
					}
				});
			} else if (isBothYaxisBar) {
				chartSeriesData.seriesData.map((data, idx) => {
					if (idx == 0) {
						data.type = 'column';
						data.color = chartSeriesColor1;
					}
					if (idx == 1) {
						data.type = 'column';
						data.color = chartSeriesColor2;
						data.dataLabels = { ...data.dataLabels, color: 'black' };
					}
				});
			} else {
				chartSeriesData.seriesData.map((data, idx) => {
					const actualChartType = returnHighChartsChartType(props.chartType);
					if (idx == 0) {
						data.type = actualChartType as
							| 'bar'
							| 'column'
							| 'scatter'
							| 'wordcloud'
							| 'line'
							| 'packedbubble'
							| 'columnrange'
							| 'funnel'
							| 'area'
							| 'lollipop'
							| 'pyramid'
							| 'solidgauge'
							| 'heatmap'
							| 'sankey'
							| 'dependencywheel'
							| 'treemap'
							| undefined;
						data.color = chartSeriesColor1;
					}
					if (idx == 1) {
						if (props.secondChartType === 'bar') {
							data.type = 'column';
						}
						if (props.secondChartType === 'line') {
							data.type = 'line';
						}
						data.color = chartSeriesColor2;
						data.dataLabels = { ...data.dataLabels, color: 'black' };
					}
				});
			}
		}

		return chartSeriesData;
	};

	// Function to update chart options
	const updateChartOptions = async () => {
		const defaultChartType = props.isSingleSeries ? 'column' : 'clusteredcolumn';
		const dbChartType =
			props.chartType != undefined
				? props.isSingleSeries && (props.chartType as string) == 'solidgauge'
					? 'odometer'
					: props.isSingleSeries && props.chartType == 'treemap'
					? 'treemapsquarified'
					: !props.isSingleSeries && props.chartType == 'treemap'
					? 'treemapstripes'
					: props.chartType
				: defaultChartType;
		const isHeatmapChart = props.chartType == 'heatmap';
		const isTreemapChart =
			props.chartType == 'treemapsquarified' || props.chartType == 'treemapstripes';
		const isClusteredLineChart = props.chartType == 'clusteredline';

		const chartWithCategories: ChartWithCategories = {
			xAxiscategories: xAxisArray ?? [],
			yAxiscategories: yAxisArray ?? [],
			isHeatmapChart: isHeatmapChart,
			isTreemapChart: isTreemapChart,
			isClusteredLineChart: isClusteredLineChart,
		};

		const currentSeriesData = getSeriesDataHelper().seriesData;
		let subtitle = '';

		const actualBeginDate: TDateRange = previousSelectedStartPeriod
			? JSON.parse(previousSelectedStartPeriod)
			: selectedStartDate?.begin_date ?? 0;
		const actualEndDate: TDateRange = previousSelectedEndPeriod
			? JSON.parse(previousSelectedEndPeriod)
			: selectedEndDate?.end_date ?? 0;

		const startDate = DatePeriodFormatter(actualBeginDate?.begin_date ?? 0);
		const endDate = DatePeriodFormatter(actualEndDate?.end_date ?? 0);

		const dateSeriesField = props.droppedFields?.find(
			(x) => x.entityType == OptionsBuilderItemTypes.DateSeries
		);

		subtitle = startDate + ' to ' + endDate;

		if (dateSeriesField) {
			if (dateSeriesField.data?.previousPeriods) {
				subtitle = adjustMonthsInRange(
					subtitle,
					dateSeriesField.data?.previousPeriods,
					false
				);
			}

			if (dateSeriesField.data?.futurePeriods) {
				subtitle = adjustMonthsInRange(subtitle, dateSeriesField.data?.futurePeriods, true);
			}
		}

		subtitle += '<br/>';

		if (props.filters && props.filters.length > 0) {
			const actualFilters = (props.filters as DraggableMenuItemData[])?.filter(
				(x) => x.id != '-1'
			) as DraggableMenuItemData[];

			if (actualFilters && actualFilters.every((f) => f.data)) {
				let idx = 0;

				for (const filter of actualFilters ?? []) {
					const filterValueIds = filter.data?.value?.split(',') ?? [];
					const filterCount = filterValueIds.length;

					// build common prefix
					const prefix =
						(filter.data?.excluded ? 'Exclude ' : 'Include ') +
						(filter.data?.title ?? '') +
						': ';

					// Begin bracket for this filter
					subtitle += ' [ ' + prefix;

					// CASE 1: always show actual name if only 1 value (even when >3 filters)
					if (filterCount === 1) {
						if (filter.entityType === 'Dimension') {
							const response = await Dimension.RetrievalAsync(
								+(filter.data?.id ?? 0)
							);
							const match = response.find((item) => item.id === +filterValueIds[0]);
							if (match) subtitle += match.value;
						} else {
							const response = await DimensionAttribute.RetrievalAsync(
								+(filter.data?.id ?? 0)
							);
							const match = response.find((item) => item.id === +filterValueIds[0]);
							if (match) subtitle += match.value;
						}
					}
					// CASE 2: if <=3 filters and count <5, list the actual values
					else if (actualFilters.length <= 3 && filterCount > 1 && filterCount < 5) {
						if (filter.entityType === 'Dimension') {
							const response = await Dimension.RetrievalAsync(
								+(filter.data?.id ?? 0)
							);
							const csvValues = filterValueIds
								.map((id) => {
									const match = response.find((item) => item.id === +id);
									return match ? match.value : null;
								})
								.filter((v) => v !== null)
								.join(',');
							subtitle += csvValues;
						} else {
							const response = await DimensionAttribute.RetrievalAsync(
								+(filter.data?.id ?? 0)
							);
							const csvValues = filterValueIds
								.map((id) => {
									const match = response.find((item) => item.id === +id);
									return match ? match.value : null;
								})
								.filter((v) => v !== null)
								.join(',');
							subtitle += csvValues;
						}
					}
					// CASE 3: otherwise show count summary
					else {
						subtitle += `${filterCount} ${filter.data?.title ?? ''}'s`;
					}

					// End bracket for this filter
					subtitle += ' ]';
					idx++;
				}

				// is Dashboard filters
			} else if (
				props.filters &&
				props.filters?.length > 0 &&
				(props.filters as TDataframeFilter[])
			) {
				const actualFilters = props.filters as TDataframeFilter[];

				for (const filter of actualFilters ?? []) {
					const filterValueIds = filter.value?.split(',') ?? [];
					const filterCount = filterValueIds.length;
					let actualFilterName = '';

					const prefix =
						(filter.excluded ? 'Exclude ' : 'Include ') + '{filterPlaceholder}' + ': ';

					// Begin bracket for this filter
					subtitle += ' [ ' + prefix;

					// CASE 1: always show actual name if only 1 value (even when >3 filters)
					if (filterCount === 1) {
						if (filter.entity_type === 'Dimension') {
							const response = await Dimension.RetrievalAsync(
								+(filter?.entity_id ?? 0)
							);
							const match = response.find((item) => item.id === +filterValueIds[0]);
							if (match) subtitle += match.value;

							const dimDetails = await Dimension.GetAsync(filter.entity_id);
							actualFilterName = dimDetails.display_name ?? dimDetails.name;
						} else {
							const response = await DimensionAttribute.RetrievalAsync(
								+(filter?.entity_id ?? 0)
							);
							const match = response.find((item) => item.id === +filterValueIds[0]);
							if (match) subtitle += match.value;

							const dimDetails = await DimensionAttribute.GetAsync(filter.entity_id);
							actualFilterName = dimDetails.name;
						}
						subtitle = subtitle.replace('{filterPlaceholder}', actualFilterName);
					}
					// CASE 2: if <=3 filters and count <5, list actual names
					else if (actualFilters.length <= 3 && filterCount > 1 && filterCount < 5) {
						if (filter.entity_type === 'Dimension') {
							const response = await Dimension.RetrievalAsync(
								+(filter?.entity_id ?? 0)
							);
							const csvValues = filterValueIds
								.map((id) => {
									const match = response.find((item) => item.id === +id);
									return match ? match.value : null;
								})
								.filter((v) => v !== null)
								.join(',');
							subtitle += csvValues;

							const dimDetails = await Dimension.GetAsync(filter.entity_id);
							actualFilterName = dimDetails.display_name ?? dimDetails.name;
						} else {
							const response = await DimensionAttribute.RetrievalAsync(
								+(filter?.entity_id ?? 0)
							);
							const csvValues = filterValueIds
								.map((id) => {
									const match = response.find((item) => item.id === +id);
									return match ? match.value : null;
								})
								.filter((v) => v !== null)
								.join(',');
							subtitle += csvValues;

							const dimDetails = await DimensionAttribute.GetAsync(filter.entity_id);
							actualFilterName = dimDetails.name;
						}
						subtitle = subtitle.replace('{filterPlaceholder}', actualFilterName);
					}
					// CASE 3: otherwise show count summary
					else {
						if (filter.entity_type === 'Dimension') {
							const dimDetails = await Dimension.GetAsync(filter.entity_id);
							actualFilterName = dimDetails.display_name ?? dimDetails.name;
						} else {
							const dimDetails = await DimensionAttribute.GetAsync(filter.entity_id);
							actualFilterName = dimDetails.name;
						}
						subtitle += `${filterCount} ${actualFilterName ?? ''}'s`;
						subtitle = subtitle.replace('{filterPlaceholder}', actualFilterName);
					}

					// End bracket for this filter
					subtitle += ' ]';
				}
			}
		}

		if (!props.filters || props.filters.length == 0) {
			subtitle = '';
		}

		const resetChartDataLimitSetting = () => {
			props.setChartSettings((prev: ZiChartSettings) => ({
				...prev,
				limitResults: 0,
			}));

			props.setChartType && props.setChartType('bar');
		};

		let chartOptionsBuilder = new ChartBuilder()
			.withTitle(props.chartSettings.hideTitle ? '' : title, subtitle)
			.withCategoryTitle(props.categoryTitle, isTreemapChart)
			.withValueCategories(chartWithCategories)
			.withSeries(
				dbChartType,
				currentSeriesData,
				chartSeriesColor1,
				!props.isSingleSeries,
				props.unitType,
				props.selected2ndUnitType,
				props.ApplyDrilldownDrillIn && props.ApplyDrilldownDrillIn,
				lookupArray,
				props.droppedFacts,
				props.secondChartType === 'line',
				props.chartSettings.showPercentages,
				props.selectedNumDecimals,
				undefined,
				enableHighchartsAccessibility,
				colorPalette,
				resetChartDataLimitSetting
			);

		if (props.noAnimation) {
			chartOptionsBuilder.withoutAnimation();
		}

		const showSecondYaxis = props.droppedFacts
			? props.droppedFacts.length > 1 &&
			  dbChartType !== ChartTypesEnum.Columnrange &&
			  (dbChartType as string) !== `${ChartTypesEnum.Treemap}squarified`
			: false;

		if (isHeatmapChart || isTreemapChart) {
			const { lightenedHex, darkenedHex } = getShadedColors(chartSeriesColor1, 18, 40);
			chartOptionsBuilder = chartOptionsBuilder.withColorAxis({
				minColor: lightenedHex,
				maxColor: darkenedHex,
				showInLegend:
					props.chartType !== 'treemapsquarified' && props.chartType !== 'treemapstripes',
			});
		}

		const showLegend =
			props.chartType == 'sankey' || props.chartType == 'dependencywheel'
				? false
				: !props.chartSettings.hideLegend;

		const seriesHeaders =
			props.chartRetrevialData && props.chartRetrevialData.length > 0
				? Object.keys(props.chartRetrevialData[0])
				: [];

		const yAxisFirstUnitType =
			props.droppedFacts && props.droppedFacts.length > 0
				? props.unitTypes?.find((x) =>
						props.droppedFacts ? x.id == props.droppedFacts[0].data?.unitType : 0
				  )?.name
				: '';

		const yAxisFirstHeaderValue =
			seriesHeaders.length > 2 && props.droppedFacts && props.droppedFacts.length == 1 //Check if multi-series chart (2 series and a fact)
				? seriesHeaders[1].replace(/([a-z])([A-Z])/g, '$1 $2')
				: yAxisFirstUnitType;

		const xAxisFirstHeaderValue =
			seriesHeaders.length > 1 ? seriesHeaders[0].replace(/([a-z])([A-Z])/g, '$1 $2') : '';

		const yAxisSecondUnitType =
			props.droppedFacts && props.droppedFacts.length > 1
				? props.unitTypes?.find((x) =>
						props.droppedFacts ? x.id == props.droppedFacts[1].data?.unitType : 0
				  )
				: '';
		const yAxisSecondHeaderValue = yAxisSecondUnitType ? yAxisSecondUnitType.name : '';

		chartOptionsBuilder = chartOptionsBuilder
			.withDataLabels(props.chartSettings.showDataValues, showLegend, dbChartType)
			.withScrollbar(props.chartSettings.showScrollbar)
			.withValueAxisLabelsShown(
				!props.chartSettings.hideYAxis,
				!props.chartSettings.hideXAxis,
				currentSeriesData,
				props.unitType,
				props.selected2ndUnitType,
				props.isSingleSeries,
				dbChartType,
				showSecondYaxis,
				yAxisFirstHeaderValue,
				yAxisSecondHeaderValue,
				xAxisFirstHeaderValue,
				props.chartSettings.showPercentages,
				props.selectedNumDecimals,
				props.selected2ndNumDecimals,
				props.droppedFacts
			);

		if (props.transparentBackground) {
			chartOptionsBuilder.withTransparentBackground();
		}

		if (props.size) {
			chartOptionsBuilder.withSize(props.size.height, props.size.width);
		}

		setOptions(chartOptionsBuilder.build());
	};

	const updateChartOptionsWrapper = () => {
		// Call the initial logic
		updateChartOptions();
		setHasLoadedChartOptions(true);
	};

	// No need to set loading for these chart option changes
	useEffect(() => {
		updateChartOptionsWrapper();
	}, [
		props.chartType,
		backgroundColor,
		props.chartSettings,
		props.chartRetrevialData,
		seriesData,
		props.valueTitle,
		chartSeriesColor1,
		enableHighchartsAccessibility,
		colorPalette,
		actualHumanifyChartNumbers,
		props.isDataRetrievalLoading,
	]);

	useEffect(() => {
		if (executionCount >= 2) {
			setTimeout(() => {
				if (isLoading) {
					setIsLoading(false);
				}
			}, 600);
			setExecutionCount(0);
		}
	}, [executionCount]);

	useEffect(() => {
		const newOptions = { ...options };
		if (newOptions.chart && props.size?.height) {
			newOptions.height = props.size?.height;
			newOptions.width = props.size?.width;
		}
		setOptions({ ...newOptions });
	}, [props.size]);

	useEffect(() => {
		setDisplayStateNoResults('none');

		const isDashboardView = window.location.pathname.includes('analyze/dashboards/view');
		const isDashboardChartView =
			window.location.pathname.includes('analyze/dashboards/') &&
			window.location.pathname.includes('/chart/') &&
			window.location.pathname.includes('/view');
		const isChartView =
			window.location.pathname.includes('analyze/charts/view/') || isDashboardChartView;
		const hasLoadedFOrChartView = isChartView ? isChartView && isPageLoadChartView : true;
		const dashboardViewExtraLoadConditions =
			isDashboardView &&
			(props.chartType == 'odometer' ||
				props.chartData?.name.toLowerCase().includes('time to fill'));
		const timeout = !hasLoadedFOrChartView
			? 6000
			: isDashboardView
			? 5000
			: isChartView
			? 0
			: 0;

		//allow buffer period since DR is complete by highcharts is still transpiling data into highcharts render format
		if (isLoading || xAxisArray.length == 0) {
			setLoadingChartOverride(true);
			setTimeout(() => {
				setLoadingChartOverride(false);
			}, timeout);
		}

		if (isChartView && xAxisArray.length > 0) {
			setIsPageLoadChartView(true);
		}

		// Only show "No results" if chart finished loading AND there is no data
		if (!isLoading && xAxisArray.length === 0) {
			setTimeout(() => {
				setDisplayStateNoResults('initial');
				setLoadingChartOverride(false);
			}, 3500);
		} else {
			setDisplayStateNoResults('none');
		}
	}, [isLoading, xAxisArray]);

	useEffect(() => {
		const chartType = props.chartData?.chart_type;
		const realChartType =
			props.chartType &&
			(props.chartType === 'treemapsquarified' || props.chartType === 'treemapstripes'
				? 'treemap'
				: props.chartType);

		const isMultiseries = localStorage.getItem('currentSeriesState') ?? previousSeriesState;
		const chartTypeHasChanged = props.chartSeriesHasChanged;
		setPreviousSeriesState(isMultiseries);

		if (chartTypeHasChanged && props.droppedFields && props.droppedFields.length < 2) {
			setSelectedChartTypeButton('bar'); // reset to default single-series type
			props.setChartType && props.setChartType('bar' as ChartSeriesChartTypes);
			return;
		}

		if (!chartType) {
			setSelectedChartTypeButton('column');
		} else if (isMultiseries && chartTypeHasChanged && realChartType == 'clusteredcolumn') {
			setSelectedChartTypeButton('clusteredcolumn');
		} else if (realChartType && props.droppedFields && props.droppedFields.length < 2) {
			setSelectedChartTypeButton(realChartType);
		} else if (!chartTypeHasChanged) {
			const newChartType = realChartType == 'treemap' ? props.chartType : realChartType;
			setSelectedChartTypeButton(newChartType);
		}
		props.setMustResetSelectedChart && props.setMustResetSelectedChart(false);
	}, [chartTypeOpen]);

	useEffect(() => {
		setTimeout(() => {
			handleSeriesTypeChange();
		}, 1000);
	}, [props.droppedFields]);

	const handleSeriesTypeChange = () => {
		const isMultiSeries = props.droppedFields && props.droppedFields.length > 1;
		const seriesType = isMultiSeries ? 'multi' : 'single';
		localStorage.setItem('currentSeriesState', seriesType);
	};

	const ChartTypeButton_Click = (
		event: MouseEvent | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>,
		type: string | undefined,
		name: string
	): void => {
		const isMultiFact = props.droppedFacts && props.droppedFacts?.length > 1;
		handleSeriesTypeChange();

		// Set chart type
		//workaround fix for if you are changing the chart from bar to column and the chart is multi-fact
		if (type == 'column' && isMultiFact) {
			setIsLoading(true);
			props.setChartType && props.setChartType('scatter');
			setTimeout(() => {
				props.setChartType && props.setChartType('column');
				setIsLoading(false);
			}, 50);
		} else {
			props.setChartType && props.setChartType(type as ChartSeriesChartTypes);
		}

		// Remove class from all other buttons
		const buttons = document.getElementsByClassName('btn-charttype');
		const buttonElements = Array.from(buttons) as HTMLButtonElement[];
		buttonElements.map((x) => {
			x.classList.remove('btn-chart-selected');
		});

		const currentButton = event.currentTarget as HTMLButtonElement;
		setSelectedChartTypeButton(name);
		currentButton && currentButton.classList.add('btn-chart-selected');
	};

	// endregion state

	// region tooltips
	// chart config
	const chartTypeTooltipRef = useRef();
	const drilldownTooltipRef = useRef();
	const colorsTooltipRef = useRef();
	const chartSettingsTooltipRef = useRef();
	const insightsTooltipRef = useRef();

	// chart types
	const barTooltip = useRef();
	const columnTooltip = useRef();
	const scatterTooltip = useRef();
	const wordCloudTooltip = useRef();
	const lineTooltip = useRef();
	const pieTooltip = useRef();
	const donutTooltip = useRef();
	const packedBubbleTooltip = useRef();
	const funnelTooltip = useRef();
	const areaTooltip = useRef();
	const lollipopTooltip = useRef();
	const pyramidTooltip = useRef();
	const odometerTooltip = useRef();
	const columnRangeTooltip = useRef();
	const treemapSquarifiedTooltip = useRef();
	const heatmapTooltip = useRef();
	const stackedBarTooltip = useRef();
	const clusteredlineTooltip = useRef();
	const stackedColumnTooltip = useRef();
	const clusteredBarTooltip = useRef();
	const clusteredColumnTooltip = useRef();
	const sankeyTooltip = useRef();
	const depWheelTooltip = useRef();
	const treemapStripesTooltip = useRef();
	// endregion tooltips

	const toggleItem = (
		key:
			| 'type'
			| 'drill'
			| 'colors'
			| 'settings'
			| 'insights'
			| 'drillOut'
			| 'secondYaxisBar'
			| 'secondYaxisLine'
	): void => {
		switch (key) {
			case 'type':
				setChartTypeOpen((prev) => !prev);
				break;
			case 'drill':
				props.setDrillSequenceOpen && props.setDrillSequenceOpen((prev) => !prev);
				break;
			case 'colors':
				setColorsOpen((prev) => !prev);
				break;
			case 'settings':
				setChartSettingsOpen((prev) => !prev);
				break;
			case 'insights':
				props.setInsightsOpen && props.setInsightsOpen((prev) => !prev);
				break;
			case 'drillOut':
				props.resetDrillSequence && props.resetDrillSequence();
				break;
			case 'secondYaxisBar':
				props.setSecondChartType && props.setSecondChartType('bar');
				break;
			case 'secondYaxisLine':
				props.setSecondChartType && props.setSecondChartType('line');
				break;
		}
	};

	const handleOverrideValueDefinedColors = (
		checked: boolean,
		event: React.FormEvent<HTMLInputElement>
	) => {
		setOverrideDefinedColors(checked);
		props.setChartSettings &&
			props.setChartSettings({
				...props.chartSettings,
				overrideValueDefinedColors: checked,
			});
	};

	const handleChangeComplete = (color: ColorResult) => {
		const { hex } = color;
		setBackgroundColor(hex);

		const { lightenedHex, darkenedHex } = getShadedColors(hex, 18, 40);

		if (currentChartSeriesNumber == 1) {
			setChartSeriesColor1(hex);

			if (props.chartSettings.chartColor.includes('|')) {
				const splitColors = props.chartSettings.chartColor.trim().split('|');
				const secondSeriesColors = splitColors[1];

				props.setChartSettings &&
					props.setChartSettings({
						...props.chartSettings,
						chartColor: hex + ' | ' + secondSeriesColors,
					});
			} else {
				props.setChartSettings &&
					props.setChartSettings({
						...props.chartSettings,
						chartColor: hex,
					});
			}
		}

		if (currentChartSeriesNumber == 2) {
			setChartSeriesColor2(hex);

			const existingChartSeriesColor = props.chartSettings.chartColor;

			if (props.chartSettings.chartColor.includes('|')) {
				const splitColors = existingChartSeriesColor.trim().split('|');
				const firstSeriesColor = splitColors[0].trim();
				props.setChartSettings &&
					props.setChartSettings({
						...props.chartSettings,
						chartColor: firstSeriesColor + ' | ' + hex,
					});
			} else {
				props.setChartSettings &&
					props.setChartSettings({
						...props.chartSettings,
						chartColor: props.chartSettings.chartColor + ' | ' + hex,
					});
			}
		}
	};

	function getShadedColors(hexColor: string, lightenAmount: number, darkenAmount: number) {
		const baseColor = tinycolor(hexColor);

		// Lighten the color
		const lightenedColor = baseColor.lighten(lightenAmount);
		const lightenedHex = lightenedColor.toHexString();

		// Darken the color
		const darkenedColor = baseColor.darken(darkenAmount);
		const darkenedHex = darkenedColor.toHexString();

		return { lightenedHex, darkenedHex };
	}

	let singleSeries = [
		{
			type: 'bar',
			icon: faChartSimpleHorizontal,
			ref: barTooltip,
			name: 'bar',
		},
		{
			type: 'column',
			icon: faChartSimple,
			ref: columnTooltip,
			name: 'column',
		},
		{
			type: 'scatter',
			icon: faChartScatter,
			ref: scatterTooltip,
			name: 'scatter',
		},
		{
			type: 'wordcloud',
			icon: faCloudWord,
			ref: wordCloudTooltip,
			name: 'wordcloud',
		},
		{ type: 'line', icon: faChartLineUp, ref: lineTooltip, name: 'line' },
		{ type: 'pie', icon: faPieChart, ref: pieTooltip, name: 'pie' },
		{ type: 'donut', icon: faCircleDot, ref: donutTooltip, name: 'donut' },
		{
			type: 'packedbubble',
			icon: faChartScatterBubble,
			ref: packedBubbleTooltip,
			name: 'scatterPlot',
		},
		{ type: 'funnel', icon: faFilter, ref: funnelTooltip, name: 'funnel' },
		{ type: 'area', icon: faChartArea, ref: areaTooltip, name: 'area' },
		{
			type: 'lollipop',
			icon: faLollipop,
			ref: lollipopTooltip,
			name: 'lollipop',
		},
		{
			type: 'pyramid',
			icon: faChartPyramid,
			ref: pyramidTooltip,
			name: 'pyramid',
		},
		{
			type: 'treemapsquarified',
			icon: faChartTreeMap,
			ref: treemapSquarifiedTooltip,
			name: 'treemapsquarified',
		},
	];

	singleSeries = [
		...singleSeries,
		...(xAxisArray.length == 1
			? [
					{
						type: 'odometer',
						icon: faGauge,
						ref: odometerTooltip,
						name: 'odometer',
					},
			  ]
			: []),
	];

	singleSeries = [
		...singleSeries,
		...(props.droppedFacts && props.droppedFacts.length > 1
			? [
					{
						type: 'columnrange',
						icon: faSlider,
						ref: columnRangeTooltip,
						name: 'columnrange',
					},
			  ]
			: []),
	];

	// region popovers

	const chartTypeButton = (
		<Popover
			headerContent={'Chart Type'}
			bodyContent={
				<>
					<span>Single Series</span>
					<div className="chart-type-container">
						{singleSeries.map((button, index) => (
							<Button
								className={`btn-charttype ${
									selectedChartTypeButton === button.type
										? 'btn-chart-selected'
										: ''
								}`}
								id={`${button.type}_Ref_${button.icon.iconName}`}
								key={index}
								name={button.type}
								ref={button.ref}
								isDisabled={!props.isSingleSeries}
								onClick={(event) =>
									ChartTypeButton_Click(
										event,
										button.type as ChartSeriesChartTypes,
										button.name
									)
								}
							>
								<FontAwesomeIcon icon={button.icon} />
							</Button>
						))}
					</div>
					<span>Multi Series</span>
					<div className="chart-type-container">
						{[
							{ type: 'heatmap', icon: faHeat, ref: heatmapTooltip, name: 'heatmap' },
							{
								type: 'stackedcolumn',
								icon: faChartSimple,
								ref: stackedColumnTooltip,
								name: 'stackedcolumn',
							},
							{
								type: 'stackedbar',
								icon: faChartSimpleHorizontal,
								ref: stackedBarTooltip,
								name: 'stackedbar',
							},
							{
								type: 'clusteredline',
								icon: faChartLineUp,
								ref: clusteredlineTooltip,
								isClustered: true,
								name: 'clusteredline',
							},
							{
								type: 'clusteredcolumn',
								icon: faChartSimple,
								ref: clusteredColumnTooltip,
								isClustered: true,
								name: 'clusteredcolumn',
							},
							{
								type: 'clusteredbar',
								icon: faChartSimpleHorizontal,
								ref: clusteredBarTooltip,
								isClustered: true,
								name: 'clusteredbar',
							},
							{
								type: 'sankey',
								icon: faDiagramSankey,
								ref: sankeyTooltip,
								name: 'sankey',
							},
							{
								type: 'dependencywheel',
								icon: faDiagramNested,
								ref: depWheelTooltip,
								name: 'dependencywheel',
							},
							{
								type: 'treemapstripes',
								icon: faChartTreeMap,
								ref: treemapStripesTooltip,
								name: 'treemapstripes',
							},
						].map((button, index) => (
							<Button
								id={`${button.type}_Ref_${button.icon.iconName}`}
								key={index}
								name={button.type}
								ref={button.ref}
								isDisabled={props.isSingleSeries}
								onClick={(event) =>
									ChartTypeButton_Click(
										event,
										button.type as ChartSeriesChartTypes,
										button.name
									)
								}
								className={`btn-charttype ${
									button.isClustered ? 'dv-clustered' : ''
								} ${
									selectedChartTypeButton === button.type
										? ' btn-chart-selected'
										: ''
								}`}
							>
								<FontAwesomeIcon icon={button.icon} />
								{button.isClustered && <FontAwesomeIcon icon={faCube} />}
							</Button>
						))}
					</div>

					{props.droppedFacts &&
						props.droppedFacts.length > 1 &&
						props.chartType !== 'columnrange' && (
							<>
								<br />
								<span>Second Y-Axis Chart Type</span>
								<div className="chart-type-container">
									<Tooltip content={'Toggle Second Y-Axis Bar Chart'}>
										<Button
											id="toggleSecondYAxisBarChart"
											data-testid="toggleSecondYAxisBarChart"
											className={`${
												props.secondChartType === 'bar'
													? 'btn-yaxis-selected btn-charttype'
													: 'btn-charttype'
											}`}
											onClick={(e) => {
												toggleItem('secondYaxisBar');
											}}
										>
											<FontAwesomeIcon icon={faChartBar} />
										</Button>
									</Tooltip>

									<Tooltip content={'Toggle Second Y-Axis Line Chart'}>
										<Button
											id="toggleSecondYAxisLineChart"
											data-testid="toggleSecondYAxisLineChart"
											className={`${
												props.secondChartType === 'line'
													? 'btn-yaxis-selected btn-charttype'
													: 'btn-charttype'
											}`}
											onClick={(e) => {
												toggleItem('secondYaxisLine');
											}}
										>
											<FontAwesomeIcon icon={faChartLineUp} />
										</Button>
									</Tooltip>
								</div>
							</>
						)}
					<>
						<Tooltip
							content={'Horizontal Bar'}
							reference={barTooltip}
						/>
						<Tooltip
							content={'Vertical Bar'}
							reference={columnTooltip}
						/>
						<Tooltip
							content={'Scatter Plot'}
							reference={scatterTooltip}
						/>
						<Tooltip
							content={'Word Cloud'}
							reference={wordCloudTooltip}
						/>
						<Tooltip
							content={'Line'}
							reference={lineTooltip}
						/>
						<Tooltip
							content={'Pie'}
							reference={pieTooltip}
						/>
						<Tooltip
							content={'Donut'}
							reference={donutTooltip}
						/>
						<Tooltip
							content={'Bubble'}
							reference={packedBubbleTooltip}
						/>
						<Tooltip
							content={'Funnel'}
							reference={funnelTooltip}
						/>
						<Tooltip
							content={'Area'}
							reference={areaTooltip}
						/>
						<Tooltip
							content={'Lollipop'}
							reference={lollipopTooltip}
						/>
						<Tooltip
							content={'Pyramid'}
							reference={pyramidTooltip}
						/>
						<Tooltip
							content={'Odometer'}
							reference={odometerTooltip}
						/>
						<Tooltip
							content={'Column Range'}
							reference={columnRangeTooltip}
						/>
						<Tooltip
							content={'Treemap Squarified'}
							reference={treemapSquarifiedTooltip}
						/>
						<Tooltip
							content={'Heatmap'}
							reference={heatmapTooltip}
						/>
						<Tooltip
							content={'Stacked Column'}
							reference={stackedColumnTooltip}
						/>
						<Tooltip
							content={'Stacked Bar'}
							reference={stackedBarTooltip}
						/>
						<Tooltip
							content={'Clustered Line'}
							reference={clusteredlineTooltip}
						/>

						<Tooltip
							content={'Clustered Column'}
							reference={clusteredColumnTooltip}
						/>
						<Tooltip
							content={'Clustered Bar'}
							reference={clusteredBarTooltip}
						/>
						<Tooltip
							content={'Sankey Diagram'}
							reference={sankeyTooltip}
						/>
						<Tooltip
							content={'Dependency Wheel'}
							reference={depWheelTooltip}
						/>
						<Tooltip
							content={'Treemap Stripes'}
							reference={treemapStripesTooltip}
						/>
					</>
				</>
			}
		>
			<>
				<Button
					onClick={() => toggleItem('type')}
					ref={chartTypeTooltipRef}
				>
					<FontAwesomeIcon
						id="chart-type"
						icon={faChartSimple}
					/>
				</Button>
				<Tooltip
					content={'Chart Type'}
					reference={chartTypeTooltipRef}
				/>
			</>
		</Popover>
	);

	const chartColorButton = (
		<Popover
			headerContent={'Colors'}
			bodyContent={
				<>
					{props.droppedFacts && props.droppedFacts.length > 1 && (
						<div>
							<Tooltip content="Primary Series Color">
								<>
									{' '}
									<Button
										className={
											currentChartSeriesNumber == 1
												? 'btn-chart-selected'
												: ''
										}
										onClick={() => {
											setCurrentChartSeriesNumber(1);
										}}
									>
										<FontAwesomeIcon icon={faChartSimple} />{' '}
										<FontAwesomeIcon icon={fa1} />
									</Button>
								</>
							</Tooltip>
							<Tooltip content="Secondary Series Color">
								<>
									{' '}
									<Button
										className={
											currentChartSeriesNumber == 2
												? 'btn-chart-selected'
												: ''
										}
										onClick={() => {
											setCurrentChartSeriesNumber(2);
										}}
									>
										<FontAwesomeIcon icon={faChartSimple} />{' '}
										<FontAwesomeIcon icon={fa2} />
									</Button>
								</>
							</Tooltip>
							<span className="spacer-sm"></span>
						</div>
					)}
					<Checkbox
						label="Use value-assigned colors"
						isChecked={overrideDefinedColors}
						onChange={handleOverrideValueDefinedColors}
						id="use-value-assigned-colors"
					/>
					&nbsp;
					<Text component="h3">
						{' '}
						<div
							className="active-color-preview"
							style={{
								backgroundColor:
									currentChartSeriesNumber == 1
										? chartSeriesColor1
										: chartSeriesColor2,
							}}
						></div>{' '}
						<span className="active-color-label">
							Current Color is{' '}
							{currentChartSeriesNumber == 1
								? chartSeriesColor1.toUpperCase()
								: chartSeriesColor2.toUpperCase()}
						</span>
					</Text>
					<span className="spacer-xs"></span>
					<CustomColorCarousel
						colors={colorPalette}
						selectedColor={backgroundColor}
						onChangeComplete={handleChangeComplete}
					/>
				</>
			}
		>
			<>
				<Button
					onClick={() => toggleItem('colors')}
					ref={colorsTooltipRef}
				>
					<FontAwesomeIcon icon={faPalette} />
				</Button>
				<Tooltip
					content={'Colors'}
					reference={colorsTooltipRef}
				/>
			</>
		</Popover>
	);

	const onLimitValueChange = (event: FormEvent<HTMLInputElement>): void => {
		const value = (event.target as HTMLInputElement).value;
		props.setChartSettings({
			...props.chartSettings,
			limitResults: +value,
		});
	};

	const chartSettingsButton = (
		<Popover
			headerContent={'Chart Settings'}
			bodyContent={
				<>
					<FormGroup label="Limit Results">
						<NumberInput
							value={props.chartSettings.limitResults}
							onMinus={() =>
								props.setChartSettings({
									...props.chartSettings,
									limitResults: Math.max(
										(props.chartSettings.limitResults ?? 1) - 1,
										0
									),
								})
							}
							onPlus={() =>
								props.setChartSettings({
									...props.chartSettings,
									limitResults: (props.chartSettings.limitResults ?? 1) + 1,
								})
							}
							onChange={onLimitValueChange}
						/>
					</FormGroup>
					<FormGroup>
						<Checkbox
							label="Show Data Value"
							isChecked={props.chartSettings.showDataValues}
							onChange={(c) =>
								props.setChartSettings({
									...props.chartSettings,
									showDataValues: c,
								})
							}
							id="showDataValue"
						/>
					</FormGroup>
					<FormGroup>
						<Checkbox
							label="Show as Percentage"
							isChecked={props.chartSettings.showPercentages}
							onChange={(c) =>
								props.setChartSettings({
									...props.chartSettings,
									showPercentages: c,
								})
							}
							id="showPercentage"
						/>
					</FormGroup>
					<FormGroup>
						<Checkbox
							label="Show Scrollbar"
							isChecked={props.chartSettings.showScrollbar}
							onChange={(c) =>
								props.setChartSettings({
									...props.chartSettings,
									showScrollbar: c,
								})
							}
							id="showScrollbar"
						/>
					</FormGroup>
					<FormGroup>
						<Checkbox
							label="Hide Y-Axis Scale"
							isChecked={props.chartSettings.hideYAxis}
							onChange={(c) =>
								props.setChartSettings({
									...props.chartSettings,
									hideYAxis: c,
								})
							}
							id="hideValueScale"
						/>
					</FormGroup>
					<FormGroup>
						<Checkbox
							label="Show Chart Legend"
							isChecked={!props.chartSettings.hideLegend}
							onChange={(c) => {
								props.setChartSettings({
									...props.chartSettings,
									hideLegend: !c,
								});
							}}
							id="showChartLegend"
						/>
					</FormGroup>
					<FormGroup>
						<Checkbox
							label={
								<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
									Prettify/Humanify Chart Numbers
									<Tooltip content="When this setting is enabled large chart values — e.g. in the thousands and millions — will be displayed in abbreviated form. Example: 2,000,000 becomes 2M.">
										<FontAwesomeIcon
											icon={faCircleInfo}
											style={{ cursor: 'pointer' }}
										/>
									</Tooltip>
								</span>
							}
							isChecked={humanifyChartNumbers}
							onChange={(c) => {
								if (c) {
									localStorage.setItem('humanifyChartNumbers', 'True');
									setActualHumanifyChartNumbers(true);
								} else {
									localStorage.setItem('humanifyChartNumbers', 'False');
									setActualHumanifyChartNumbers(false);
								}
							}}
							id="showChartHumanReadable"
						/>
					</FormGroup>
				</>
			}
		>
			<>
				<Button
					onClick={() => toggleItem('settings')}
					ref={chartSettingsTooltipRef}
				>
					<FontAwesomeIcon icon={faGear} />
				</Button>
				<Tooltip
					content={'Chart Settings'}
					reference={chartSettingsTooltipRef}
				/>
			</>
		</Popover>
	);

	// endregion popovers

	const noSeriesDataHtml = (
		<div style={{ display: displayStateNoResults }}>
			<EmptyResults
				emptyStateBody="No results match the filter criteria specified.  Try changing your date range or other filters if set."
				iconName={faInfoCircle}
				backComponent={<></>}
			/>
		</div>
	);

	const getChartType = (dbChartType: ChartSeriesChartTypes): string | undefined => {
		let chartType;

		if (selectedChartTypeButton) {
			if (
				selectedChartTypeButton == ChartTypesEnum.Columnrange &&
				props.droppedFacts &&
				props.droppedFacts.length == 1
			) {
				chartType = ChartTypesEnum.Column;
			} else {
				chartType = selectedChartTypeButton;
			}
		} else {
			chartType = dbChartType;
		}

		return chartType;
	};

	const recordLimitWarnHtml = (
		<EmptyResults
			emptyStateBody="More than 500 rows are present in the dataset. Apply filters to narrow your selection and reduce the number of rows, or consider creating a report that runs via the job queue to return all records."
			heading="Data Limit Warning"
			iconName={faWarning}
			backComponent={<></>}
		/>
	);

	return (
		<>
			<div
				className="zi-chart-container"
				id={`zi-chart-${props.chartData?.id}`}
				style={{ width: '18.75rem' }}
			>
				{props.isConfigMode && (
					<div className="chart-controls">
						{props.drillLevel && props.drillLevel - 1 > 0 && (
							<Button
								onClick={() => toggleItem('drillOut')}
								ref={chartTypeTooltipRef}
							>
								<FontAwesomeIcon
									id="chart-type"
									icon={faChevronCircleUp}
								/>{' '}
								Drill Out
							</Button>
						)}
						<>{chartTypeButton}</>
						<>
							<Button
								id="toggleSidebarBtn"
								onClick={() => toggleItem('drill')}
								ref={drilldownTooltipRef}
							>
								<FontAwesomeIcon icon={faChevronsDown} />
							</Button>
							<Tooltip
								content={'Drilldown'}
								reference={drilldownTooltipRef}
							/>
						</>
						{chartColorButton}
						<></>
						<>{chartSettingsButton}</>
						<>
							<Button
								onClick={() => toggleItem('insights')}
								ref={insightsTooltipRef}
								isDisabled={!chartId}
							>
								<FontAwesomeIcon icon={faSparkles} />
							</Button>
							<Tooltip
								content={'Insights'}
								reference={insightsTooltipRef}
							/>
						</>
					</div>
				)}
				{(props.isDataRetrievalLoading && xAxisArray.length === 0) ||
				loadingChartOverride ? (
					<div className={dashboardId ? 'dashboard-chart-loader' : ''}>
						<Loader />
					</div>
				) : null}
				{(props.isDataRetrievalLoading == false || props.isChartOnly) &&
					hasLoadedChartOptions &&
					props.showLimitWarning == false &&
					!loadingChartOverride && (
						<>{xAxisArray && xAxisArray.length > 0 ? chartRender : noSeriesDataHtml}</>
					)}

				{props.showLimitWarning && recordLimitWarnHtml}
			</div>
		</>
	);
};

export default ZiChart;
